import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import {
  AI_SUPPORT_IDLE_CHECK_MS,
  AI_SUPPORT_IDLE_CLOSE_MS,
  AI_SUPPORT_IDLE_WARNING_MS,
  AI_SUPPORT_MAX_QUESTION
}                                from './ai-support.constants';
import { AiSupportMessageModel } from './typings/ai-support.message';
import { AiSupportReplyModel }   from './typings/ai-support.reply';
import { AiSupportService }      from './ai-support.service';
import { CompanyService }        from '../companies/company.service';
import { ConfirmService }        from '../messages/confirm/confirm.service';
import { MessagesService }       from '../messages/messages.service';

@Component({
  selector   : 'wor-ai-support-panel',
  styles: [ require('./ai-support.panel.component.scss') ],
  template: require('./ai-support.panel.component.html')
})
export class AiSupportPanelComponent implements AfterViewChecked, OnDestroy, OnInit {
  private _deregister  : Function                       = null;
  private _generation  : number                         = 0;
  private _idleTimer   : ReturnType<typeof setInterval> = null;
  private _lastActivity: number                         = 0;
  private _onHashChange: () => void                     = () => this._checkFlag();
  private _shouldScroll: boolean                        = false;

  conversationId    : string | null                = null;
  errorMessage      : string                       = '';
  hasIdleNotice     : boolean                      = false;
  isClosedForIdle   : boolean                      = false;
  isContinuing      : boolean                      = false;
  isConversationFull: boolean                      = false;
  isEscalated       : boolean                      = false;
  isEscalating      : boolean                      = false;
  isLimited         : boolean                      = false;
  isLoading         : boolean                      = false;
  maxQuestion       : number                       = AI_SUPPORT_MAX_QUESTION;
  messages          : Array<AiSupportMessageModel> = [];
  question          : string                       = '';
  reference         : string                       = '';

  @HostBinding('class.is-open') isOpen : boolean = false;

  @ViewChild('thread') thread : ElementRef;

  constructor (
    private aiSupportService: AiSupportService,
    private companyService  : CompanyService,
    private confirmService  : ConfirmService,
    private messagesService : MessagesService,
    private router          : UIRouter,
    private translateService: TranslateService,
    private zone            : NgZone
  ) {}

  private _checkFlag () : void {
    if ( this.isAvailable() && this.aiSupportService.hasOpenFlag() ) {
      this.open();
      this.aiSupportService.removeOpenFlag();
    }
  }

  private _checkIdle () : void {
    if (!this._lastActivity) {
      return;
    }

    const idle = Date.now() - this._lastActivity;

    if ( idle >= AI_SUPPORT_IDLE_CLOSE_MS ) {
      this._closeConversation();

      this.isClosedForIdle = true;
    }
    else {
      this.hasIdleNotice = idle >= AI_SUPPORT_IDLE_WARNING_MS;
    }
  }

  private _closeConversation () : void {
    const conversationId = this.conversationId;

    this._reset();

    if (conversationId) {
      this.aiSupportService.close(conversationId)
      .catch(error => this.messagesService.error(error));
    }
  }

  // A paused answer is continued straight away until it finishes.
  private _follow ( reply : AiSupportReplyModel, generation : number ) : Promise<void> {
    if ( generation !== this._generation ) {
      return Promise.resolve();
    }

    this._onReply(reply);

    if (!reply.isContinuing) {
      return Promise.resolve();
    }

    return this.aiSupportService.continue(this.conversationId)
    .then(next => this._follow(next, generation));
  }

  private _onReply ( reply : AiSupportReplyModel ) : void {
    this.conversationId     = reply.conversationId || this.conversationId;
    this.isContinuing       = reply.isContinuing;
    this.isConversationFull = reply.isConversationFull;
    this.isLimited          = reply.isLimited;

    if (reply.message) {
      this.messages = [ ...this.messages, reply.message ];
    }

    this._touch();
  }

  private _reset () : void {
    this._generation++;
    this._lastActivity = 0;

    this.conversationId     = null;
    this.errorMessage       = '';
    this.hasIdleNotice      = false;
    this.isClosedForIdle    = false;
    this.isContinuing       = false;
    this.isConversationFull = false;
    this.isEscalated        = false;
    this.isEscalating       = false;
    this.isLimited          = false;
    this.isLoading          = false;
    this.messages           = [];
    this.reference          = '';
  }

  private _touch () : void {
    this._lastActivity = Date.now();
    this._shouldScroll = true;

    this.hasIdleNotice   = false;
    this.isClosedForIdle = false;
  }

  // Closing ends the conversation, so the panel always reopens on a fresh one.
  close () : void {
    this._closeConversation();

    this.isOpen = false;
  }

  escalate () : void {
    const conversationId = this.conversationId;
    const generation     = this._generation;

    this.confirmService.generic(this.translateService.instant('AI_SUPPORT_SPACE.PANEL.CONFIRM_SEND'))
    .then(() => {
      this.isEscalating = true;

      return this.aiSupportService.escalate(conversationId);
    })
    .then(reference => {
      if ( generation === this._generation ) {
        this.isEscalated = true;
        this.reference   = reference;

        this._touch();
      }
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => this.isEscalating = false);
  }

  isAvailable () : boolean {
    return this.companyService.hasAiSupport();
  }

  isEscalationOffered () : boolean {
    const latest = this.messages[ this.messages.length - 1 ];

    return !this.isEscalated
      && !!latest
      && !latest.isUser
      && latest.isEscalationOffered;
  }

  newConversation () : void {
    this._closeConversation();
  }

  ngAfterViewChecked () : void {
    if ( this._shouldScroll && this.thread ) {
      const element = this.thread.nativeElement;

      element.scrollTop  = element.scrollHeight;
      this._shouldScroll = false;
    }
  }

  ngOnDestroy () : void {
    if (this._deregister) {
      this._deregister();
    }

    clearInterval(this._idleTimer);
    window.removeEventListener('hashchange', this._onHashChange);

    this.aiSupportService.releaseOpenLinks();
  }

  ngOnInit () : void {
    if (!this.isAvailable()) {
      return;
    }

    this._checkFlag();

    this.aiSupportService.interceptOpenLinks(() => this.zone.run(() => this.open()));

    this._deregister = this.router.transitionService.onSuccess({}, () => this._checkFlag());
    this._idleTimer  = setInterval(() => this._checkIdle(), AI_SUPPORT_IDLE_CHECK_MS);

    window.addEventListener('hashchange', this._onHashChange);
  }

  // A confirm modal open over the page owns Escape.
  @HostListener('document:keydown.escape')
  onEscape () : void {
    if ( this.isOpen && !document.body.classList.contains('modal-open') ) {
      this.close();
    }
  }

  onQuestionKeydown ( event : KeyboardEvent ) : void {
    if (event.shiftKey) {
      return;
    }

    event.preventDefault();

    this.send();
  }

  open () : void {
    this.isOpen        = true;
    this._shouldScroll = true;

    this.aiSupportService.closeHelpCentre();
  }

  send () : void {
    const generation = this._generation;
    const question   = this.aiSupportService.cleanQuestion(this.question);

    if ( !question || this.isLoading || this.isLimited ) {
      return;
    }

    const screen      = this.aiSupportService.screenFor(this.router.globals.current.name, this.router.globals.params);
    const userMessage = this.aiSupportService.createUserMessage(question);

    this.errorMessage = '';
    this.isLoading    = true;
    this.messages     = [ ...this.messages, userMessage ];
    this.question     = '';

    this._touch();

    this.aiSupportService.ask(this.conversationId, question, screen)
    .then(reply => this._follow(reply, generation))
    .catch(error => {
      if ( generation !== this._generation ) {
        return;
      }

      this.errorMessage = error?.message || this.translateService.instant('AI_SUPPORT_SPACE.PANEL.ERROR');

      // A continuation may still finish on the server, so resending could count the question twice.
      if (!this.isContinuing) {
        this.messages = this.messages.filter(message => message !== userMessage);
        this.question = question;
      }
    })
    .finally(() => {
      if ( generation === this._generation ) {
        this.isContinuing = false;
        this.isLoading    = false;
      }
    });
  }

  trackByMessage ( index : number, message : AiSupportMessageModel ) : number {
    return message.id;
  }
}
